'use client';
import { useEffect, useContext, useState } from 'react';
import { SessionContext } from '@benzinga/session-context';
import { TrackingManager } from '@benzinga/tracking-manager';
import { handlePaywallLoginRedirect } from '@benzinga/auth-ui';
import { EDGE_CHECKOUT_URL, useBenzingaEdge } from '@benzinga/edge';

export const PermissionOverlay = ({ children }: { children: React.ReactNode }) => {
  const session = useContext(SessionContext);
  const { hasEdgeSubscription, isEdgeSubscriptionLoading, isLoggedIn } = useBenzingaEdge();
  const [hasTrackedView, setHasTrackedView] = useState(false);

  const showOverlay = !hasEdgeSubscription && !isEdgeSubscriptionLoading;

  const paywallId = `edge-dashboard-paywall`;
  const paywallCampaign = 'dashboard-locked';
  const adType = 'dashboard';
  const urlParams = new URLSearchParams({
    utm_adType: adType,
    utm_campaign: paywallCampaign,
  });

  const checkoutUrl = `${EDGE_CHECKOUT_URL}?${urlParams.toString()}`;
  const handleLoginRedirect = () => handlePaywallLoginRedirect(paywallId);

  useEffect(() => {
    if (!hasEdgeSubscription && !isEdgeSubscriptionLoading && !hasTrackedView) {
      setHasTrackedView(true);
      session.getManager(TrackingManager).trackPaywallEvent('view', {
        paywall_id: paywallId,
        paywall_type: 'hard',
        placement: `edge-dashboard`,
        utmParams: {
          adType: adType,
          campaign: paywallCampaign,
        },
      });
    }
  }, [hasEdgeSubscription, isEdgeSubscriptionLoading, hasTrackedView, session, paywallId]);

  const contentClassName = showOverlay ? 'isolate pointer-events-none select-none' : undefined;

  return (
    <div className="relative">
      <div className={contentClassName} inert={showOverlay || undefined}>
        {children}
      </div>
      {showOverlay && (
        <div className="absolute inset-0 z-10 h-full w-full">
          <div
            className="absolute left-1/2 top-2 rounded-full bg-white/80"
            style={{
              width: '82%',
              height: '82%',
              transform: 'translateX(-50%)',
              filter: 'blur(48px)',
            }}
          />
          <div className="relative z-[1] flex h-full w-full flex-col items-center pt-10">
            <div
              role="dialog"
              aria-modal="true"
              className="pointer-events-auto sticky top-10 flex max-w-[400px] cursor-pointer flex-col items-center justify-center gap-4 rounded-lg border border-bzgray-800 bg-gradient-to-b from-[#082132] to-[#1d446a] p-4 text-center shadow-[0_12px_40px_rgba(8,33,50,0.28)] animate-fade-up animate-delay-300 animate-once"
            >
              <div className="text-2xl font-bold text-white">Access with Benzinga Edge</div>
              <div className="text-base font-normal text-[#F2F8FF]">
                Get all the latest trade ideas, insights, and tools to help you make smarter investing decisions.
              </div>
              <div>
                <a
                  href={checkoutUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded bg-bzblue-700 px-4 py-2 text-base font-bold text-white"
                >
                  Unlock Edge Dashboard
                </a>
                {!isLoggedIn && (
                  <button
                    className="relative z-10 text-center text-xs font-normal text-slate-400 hover:text-white"
                    onClick={handleLoginRedirect}
                  >
                    Already a subscriber? Login here
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
